import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  INITIAL_CHECKOUT_STATE,
  reduceCheckout,
  type CheckoutEvent,
  type CheckoutState,
} from './checkout';

// Rewrite state is limited to user inputs and checkout machine state; derived quote data stays in the engine.
export type TokenInputValues = { decimal: string; formatted: string; usd: number };
export type InputMode = 'tokenAmount' | 'usdPrice';

export const DEFAULT_TOKEN_INPUT: TokenInputValues = { decimal: '', formatted: '', usd: 0 };

export type TradeModuleStore = {
  sellInput: TokenInputValues;
  buyInput: TokenInputValues;
  inputMode: InputMode;
  checkout: CheckoutState;
  updateSellInput: (value: TokenInputValues) => void;
  updateBuyInput: (value: TokenInputValues) => void;
  setInputMode: (mode: InputMode) => void;
  /** Atomically swaps the sell and buy entries. */
  switchInputs: () => void;
  resetInputs: () => void;
  /** The only action that advances the checkout machine. */
  dispatchCheckout: (event: CheckoutEvent) => void;
  /** Restores inputs and checkout machine state. */
  reset: () => void;
};

export const useTradeModuleStore = createWithEqualityFn<TradeModuleStore>()(
  (set, get) => ({
    sellInput: DEFAULT_TOKEN_INPUT,
    buyInput: DEFAULT_TOKEN_INPUT,
    inputMode: 'tokenAmount',
    checkout: INITIAL_CHECKOUT_STATE,
    updateSellInput: (sellInput) => set({ sellInput }),
    updateBuyInput: (buyInput) => set({ buyInput }),
    setInputMode: (inputMode) => set({ inputMode }),
    switchInputs: () => {
      const { sellInput, buyInput } = get();
      set({ sellInput: buyInput, buyInput: sellInput });
    },
    resetInputs: () => set({ sellInput: DEFAULT_TOKEN_INPUT, buyInput: DEFAULT_TOKEN_INPUT }),
    dispatchCheckout: (event) => {
      const checkout = get().checkout;
      const next = reduceCheckout(checkout, event);
      // The reducer returns the same reference for ignored events; skip the no-op notification.
      if (next !== checkout) set({ checkout: next });
    },
    reset: () =>
      set({
        sellInput: DEFAULT_TOKEN_INPUT,
        buyInput: DEFAULT_TOKEN_INPUT,
        inputMode: 'tokenAmount',
        checkout: INITIAL_CHECKOUT_STATE,
      }),
  }),
  shallow,
);

export const sellInputSelector = (state: TradeModuleStore) => state.sellInput;
export const buyInputSelector = (state: TradeModuleStore) => state.buyInput;
export const checkoutSelector = (state: TradeModuleStore) => state.checkout;
export const dispatchCheckoutSelector = (state: TradeModuleStore) => state.dispatchCheckout;
export const resetSelector = (state: TradeModuleStore) => state.reset;
export const executionInputSelector = (state: TradeModuleStore) => ({
  sellInput: state.sellInput,
  buyInput: state.buyInput,
  dispatchCheckout: state.dispatchCheckout,
  resetInputs: state.resetInputs,
});
